import {
  Location,
  LocationDto,
  SearchLocation,
  Country,
  LatLng,
  CitiesDto,
  NearbyLocationDto,
  SearchLocationResponse,
  City,
  DeliveryDropOffLocationDto,
  DeliveryDropOffLocation,
  LocationDtoV2,
  DropOffLocationDtoV2,
  RHLocationResponseDto,
  RHLocationDto,
  SavedLocationDtoRH,
  CreateAddressRequest,
  CreateAddressRequestRh,
  DrivingDistanceRoutesTransformed,
  ReverseGeocode
} from '../models/locations';
import { decodePath, createLatLngBounds } from '../../utils/google-maps';

export function cityToCountry(city: string): Country {
  switch (city) {
    case 'Dubai':
    case 'Abu Dhabi':
      return {
        code: 'UAE'
      };
    case 'Jeddah':
    case 'Riyadh':
    case 'Dammam':
      return {
        code: 'KSA'
      };
    case 'Amman':
      return {
        code: 'JOR'
      };
    default:
      return {
        code: 'UNKNOWN'
      };
  }
}

export function pickupToDto(pickup: Location): LocationDto {
  return {
    coordinate: pickup.location,
    nickname: pickup.name || pickup.nickname,
    city: pickup.city,
    city_id: pickup.city_id,
    area: pickup.area,
    building_name: pickup.building,
    street: pickup.street,
    notes: pickup.note
  };
}

export function fromDto(dto: LocationDto): Location {
  return {
    id: -1,
    name: dto.display_name || dto.nickname,
    nickname: dto.nickname,
    area: dto.area,
    building: dto.building_name,
    city: dto.city,
    city_id: dto.city_id,
    location: dto.coordinate,
    street: dto.street
  };
}

export function fromDropOffDto(dto: DeliveryDropOffLocationDto): DeliveryDropOffLocation {
  return {
    id: -1,
    name: dto.display_name,
    area: dto.area,
    building: dto.building_name,
    city: dto.city,
    city_id: dto.city_id,
    location: dto.coordinate,
    street: dto.street
  };
}

export function fromDtoV2(dto: LocationDtoV2): Location {
  return {
    id: -1,
    name: dto.name,
    nickname: dto.name ?? '',
    area: dto.address?.area ?? '',
    building: dto.address?.street ?? '',
    city: dto.address?.city ?? '',
    location: {
      lat: dto.address.latitude,
      lng: dto.address.longitude
    },
    street: dto.address?.street ?? '',
    number: dto.phone_number
  };
}

export function fromDropOffDtoV2(dto: DropOffLocationDtoV2): DeliveryDropOffLocation {
  const street = dto.address?.street ?? '';
  const area = dto.address?.area ?? '';
  const city = dto.address?.city ?? '';
  const name = [street, area, city].filter(Boolean).join(' - ');
  return {
    id: -1,
    name: name,
    area,
    building: street,
    city,
    location: dto.address && {
      lat: dto.address.latitude,
      lng: dto.address.longitude
    },
    street
  };
}

export function dropoffToDto(dropoff: SearchLocation | undefined, city: string): LocationDto | undefined {
  if (!dropoff) {
    return undefined;
  }

  return {
    coordinate: dropoff.location,
    nickname: dropoff.name || 'n/a',
    city: city,
    area: dropoff.area || 'n/a',
    building_name: dropoff.building || 'n/a',
    street: dropoff.street || 'n/a'
  };
}

//TODO: refactor when migrating to new Locations stack
export function locationsToUniqueCityIds(locations: Location[], cities: City[]) {
  if (!locations?.length || !cities?.length) {
    return [1];
  }
  const citiesMap = cities.reduce((map, currCity) => map.set(currCity.name, currCity.id), new Map());
  const cityIds = locations.map(location => citiesMap.get(location.city)).filter(cityId => !!cityId);
  // @ts-ignore
  cityIds.push(1);
  return [...new Set(cityIds)];
}

export const transformDirectionsResponse =
  (pickup: LatLng, dropOff: LatLng) => (response: DrivingDistanceRoutesTransformed) => {
    function stepTransformer(step: any) {
      step.polyline = (step as any).geometry;
      return {
        travel_mode: 'DRIVING',
        path: decodePath(step.polyline).map(path => ({ lat: path.lat(), lng: path.lng() }))
      };
    }

    function legTransformer(leg: any) {
      const steps = leg.steps.map(stepTransformer);
      const firstStep = steps[0];
      const firstPath = firstStep.path[0];
      const lastStep = steps[steps.length - 1];
      const lastPath = lastStep.path[lastStep.path.length - 1];

      return {
        start_location: {
          lat: firstPath.lat,
          lng: firstPath.lng
        },
        end_location: {
          lat: lastPath.lat,
          lng: lastPath.lng
        },
        distance: {},
        duration: {},
        steps,
        traffic_speed_entry: [],
        via_waypoint: [],
        via_waypoints: []
      };
    }

    function routeTransformer(route: any) {
      return {
        bounds: {},
        legs: route.legs.map(legTransformer),
        distance: route.distance,
        duration: route.duration,
        warnings: [],
        waypoint_order: [],
        overview_path: []
      };
    }

    const routes = response.routes.map(routeTransformer).map((route: any) => {
      const bounds = createLatLngBounds();

      for (let i = 0; i < route.legs.length; i++) {
        for (let j = 0; j < route.legs[i].steps.length; j++) {
          for (let k = 0; k < route.legs[i].steps[j].path.length; k++) {
            const point = route.legs[i].steps[j].path[k];
            bounds.extend(point);
          }
        }
      }

      return {
        ...route,
        bounds
      };
    });

    return {
      routes,
      status: 'OK',
      request: {
        origin: {
          location: pickup
        },
        destination: {
          location: dropOff
        },
        travelMode: 'DRIVING'
      }
    };
  };

export function dtoToCities({ cities }: CitiesDto): City[] {
  if (!cities) {
    return [];
  }
  return cities.map(city => {
    const { id, name, name_localized, lat, lng, support, service_area_id, country } = city;
    const result: City = {
      id,
      name,
      nameLocalized: name_localized,
      country: {
        iso2: country?.iso_code_2
      },
      lat,
      lng
    };
    if (support) {
      result.support = {
        phone: support.phone
      };
    }
    if (service_area_id) {
      result.serviceAreaId = service_area_id;
    }
    return result;
  });
}

export function nearbyDtoToSearchLocation(response: NearbyLocationDto): SearchLocation {
  const { name, area, street, building, building_number, location, id, formatted_address } = response;
  return {
    id,
    name,
    area: area || street || 'n/a',
    street,
    building,
    buildingNumber: building_number,
    formattedAddress: formatted_address || `${name}, ${street}`,
    location
  };
}

export function reverseGeocodeDtoToSearchLocation(response: ReverseGeocode): SearchLocation {
  const { placeName, area, streetAddress, buildingName, longitude, latitude, id, city, unitNumber, formattedName } =
    response;
  return {
    id: `${id}`,
    name: placeName,
    area: area || streetAddress || 'n/a',
    street: streetAddress,
    building: formattedName || buildingName,
    city,
    buildingNumber: unitNumber,
    formattedAddress: formattedName || `${buildingName}, ${streetAddress}, ${city}`,
    location: { lat: latitude, lng: longitude }
  };
}

export const dtoNearbyRHToSearchLocation = (nearbyLocatioDto: RHLocationDto): SearchLocation => {
  const {
    id,
    streetAddress: street,
    placeName: name,
    area,
    buildingName: building,
    sCName,
    lat,
    lng,
    placeId,
    city
  } = nearbyLocatioDto;

  return {
    id: `${id || placeId}`,
    name,
    area: area || street || 'n/a',
    street,
    building: building || name,
    buildingNumber: '',
    formattedAddress: sCName || `${name}, ${street}`,
    location: {
      lat,
      lng
    },
    city
  };
};

export function dtoNearbyRHToSearchLocations(response: RHLocationResponseDto): SearchLocation[] {
  const { careem } = response;
  return careem.map(dtoNearbyRHToSearchLocation);
}

export function dtoRHToFirstNearbyLocation(response: RHLocationResponseDto): SearchLocation {
  const { careem } = response;
  return dtoNearbyRHToSearchLocation(careem[0]);
}

export const dtoRhToSavedLocation = (rhLocations: SavedLocationDtoRH[]): Location[] => {
  return rhLocations.map(rhLocation => {
    const {
      id,
      area,
      street,
      city,
      saved_as,
      building_name,
      latitude,
      longitude,
      location_name,
      delivery_notes,
      unit_number,
      place_id
    } = rhLocation;
    return {
      id,
      name: location_name,
      nickname: saved_as,
      city,
      area,
      street,
      location: { lat: latitude, lng: longitude },
      building: building_name,
      note: delivery_notes,
      number: unit_number,
      placeId: place_id
    };
  });
};

export function dtoToSearchLocation({ addresses }: SearchLocationResponse): SearchLocation[] {
  if (!addresses) {
    return [];
  }
  return addresses.map(
    ({
      id,
      name,
      building,
      street,
      area,
      location,
      distance,
      distance_localized,
      formatted_address,
      building_number,
      city
    }) => ({
      id,
      name,
      building,
      street,
      city,
      area: area || street || 'n/a',
      location,
      distance,
      buildingNumber: building_number,
      distanceLocalized: distance_localized,
      formattedAddress: formatted_address
    })
  );
}

export function dtoRHToSearchLocation(addresses: RHLocationDto[]): SearchLocation[] {
  if (!addresses) {
    return [];
  }
  return addresses.map(
    ({
      id,
      placeName: name,
      buildingName: building,
      streetAddress: street,
      area,
      lat,
      lng,
      dist: distanceRH,
      sCName: formatted_address,
      placeId,
      city
    }) => ({
      id: `${id || placeId}`,
      name,
      building: building || name,
      street,
      city,
      area: area || street || 'n/a',
      location: { lat, lng },
      distance: distanceRH * 1000,
      distanceLocalized: `${distanceRH.toFixed(2)} km`,
      formattedAddress: formatted_address
    })
  );
}

export const locationNameNullFallback = (locations: Location[]) =>
  locations.map(
    (location): Location =>
      location.name
        ? location
        : {
            ...location,
            name: [location.nickname, location.building, location.street, location.city].filter(Boolean).join(' - ')
          }
  );

export const createAddressRequestToRHRequest = (addressBody: CreateAddressRequest): CreateAddressRequestRh => {
  const { nickname, area, building, street, lat, lng, note, number, city, placeId } = addressBody;
  return {
    save_as: nickname,
    area,
    building_name: building,
    street,
    coordinate: {
      latitude: lat,
      longitude: lng
    },
    more_details: note,
    unit_number: number,
    city,
    place_id: placeId
  };
};
